/**
 * Cross-reload single-shot signalling for the share-link "we just
 * sent you an email" toast.
 *
 * The share-link email gate (`AuthenticationDialog`) does a full
 * `window.location.reload()` after submit, which unmounts the dialog
 * and any in-flight `notify()` call with it. To bridge the reload, we
 * stash a sentinel in `sessionStorage` at submit time (only when the
 * BE flips `isNewUser: true`), and the share-link landing surface
 * reads-and-clears the sentinel on mount via
 * `<ShareLinkInviteToast />` to fire the snackbar.
 *
 * Two invariants every caller must honour:
 *  1. Only stash when `isNewUser === true`. Returning users didn't
 *     get a new email; toasting them would lie.
 *  2. ALWAYS clear the key on read so a stale sentinel can't leak
 *     into a subsequent unrelated session.
 */

export const SHARE_LINK_INVITE_EMAIL_SENT_KEY =
  "puntt.shareLinkInviteEmailSent";

/**
 * Mark that the BE just minted an INVITED account and emailed an
 * invite link. The next surface to mount and call
 * `consumeShareLinkInviteSent()` will fire the "check your inbox"
 * toast.
 *
 * No-ops silently when sessionStorage is unavailable so the auth flow
 * isn't blocked on a locked-down browser — a missing toast is
 * strictly better than a thrown error mid-reload.
 */
export function stashShareLinkInviteSent(): void {
  try {
    sessionStorage.setItem(SHARE_LINK_INVITE_EMAIL_SENT_KEY, "1");
  } catch {
    // ignore
  }
}

/**
 * Read-and-clear the "we just sent an invite email" sentinel. Returns
 * `true` exactly once per stash so the toast can't double-fire across
 * remounts. Always clears the key on read so a stale value can't leak
 * into a future session.
 */
export function consumeShareLinkInviteSent(): boolean {
  let stored: string | null;
  try {
    stored = sessionStorage.getItem(SHARE_LINK_INVITE_EMAIL_SENT_KEY);
  } catch {
    return false;
  }
  if (!stored) return false;
  try {
    sessionStorage.removeItem(SHARE_LINK_INVITE_EMAIL_SENT_KEY);
  } catch {
    // ignore
  }
  return true;
}
